import { z } from 'zod';
import { calibrationOutcomeSchema, macroVisualDigestSchema, pixelRegionSchema, scoredCalibrationSampleSchema } from './common';

/** Historical v1 authoring audit trail only. New pipeline evidence uses the v2 report in evidencePages. */
const evidenceFields = {
  version: z.literal(1),
  radiusMs: z.literal(100),
  targetId: z.string().uuid(),
  region: pixelRegionSchema.optional(),
  presetDigest: macroVisualDigestSchema,
  frames: z.array(z.object({ id: z.string().uuid(), offsetMs: z.number().int().min(-100).max(100),
    digest: macroVisualDigestSchema }).strict()).min(1).max(8),
  samples: z.array(scoredCalibrationSampleSchema).min(1).max(96),
  outcome: calibrationOutcomeSchema,
};
// Preserve historical evidence verbatim, but never replay it with a newer scorer.
export const macroCalibrationEvidenceSchema = z.discriminatedUnion('scoringRule', [
  z.object({ ...evidenceFields, scoringRule: z.literal('raw-max-exact-inliers-10-v2'), outlierPercent: z.literal(10) }).strict(),
  z.object({ ...evidenceFields, scoringRule: z.literal('channel-envelope-v1') }).strict(),
  z.object({ ...evidenceFields, scoringRule: z.literal('channel-envelope-v2') }).strict(),
  z.object({ ...evidenceFields, scoringRule: z.literal('channel-envelope-v3') }).strict(),
  z.object({ ...evidenceFields, scoringRule: z.literal('channel-envelope-v4') }).strict(),
  z.object({ ...evidenceFields, scoringRule: z.literal('channel-envelope-v5') }).strict(),
]).superRefine((record, context) => {
  const ids = new Set(record.frames.map(frame => frame.id));
  const target = record.frames.find(frame => frame.id === record.targetId);
  if (ids.size !== record.frames.length || target?.offsetMs !== 0
    || record.frames.filter(frame => frame.offsetMs === 0).length !== 1) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Evidence requires unique frames and one target at offset zero' });
  }
  if (new Set(record.samples.map(sample => sample.caseId)).size !== record.samples.length
    || record.samples.some(sample => sample.referenceId !== record.targetId || !ids.has(sample.candidateId) || sample.split !== 'training')) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['samples'], message: 'Evidence samples must uniquely identify the captured target and candidates' });
  }
  for (const frame of record.frames.filter(frame => frame.id !== record.targetId)) {
    if (!record.samples.some(sample => sample.candidateId === frame.id && sample.transformId === 'temporal-neighbor')) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['samples'], message: 'Captured neighbor has no scoring evidence' });
    }
  }
});
export type MacroCalibrationEvidence = z.infer<typeof macroCalibrationEvidenceSchema>;

export const publishedCalibrationEvidenceSchema = z.object({
  record: macroCalibrationEvidenceSchema,
  frames: z.array(z.object({ id: z.string().uuid(), sourceUrl: z.string().url() }).strict()).min(1).max(8),
}).strict().superRefine((published, context) => {
  const ids = new Set(published.frames.map(frame => frame.id));
  if (ids.size !== published.frames.length || published.frames.length !== published.record.frames.length
    || published.record.frames.some(frame => !ids.has(frame.id))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Published evidence must retain every original frame' });
  }
});
export type PublishedCalibrationEvidence = z.infer<typeof publishedCalibrationEvidenceSchema>;
